import { UseQueryResult } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import ExpandDownArrow from 'components/DesignSystems/buttons/ExpandDownArrow';
import ExpandRightArrow from 'components/DesignSystems/buttons/ExpandRightArrow';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import { StylishNewRadio } from 'components/DesignSystems/New/StylishNewRadio';
import PratusTable from 'components/DesignSystems/PratusTable/PratusTable';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import Loading from 'components/Loading/Loading';
import { GroupRosterItem } from 'hooks/incident/useRoster';
import React from 'react';

type MemberSelectTableProps = {
  groupRoster: UseQueryResult<GroupRosterItem[], Error>;
  currentPositionMembers: RosterEntry[];
  onSelectMembers: (selectedMembers: GroupRosterItem[]) => void;
  closeTable: () => void;
  selectingLeader?: boolean;
};

const MemberSelectTable: React.FC<MemberSelectTableProps> = ({
  groupRoster,
  currentPositionMembers,
  onSelectMembers,
  closeTable,
  selectingLeader = true,
}) => {
  const [selectedMembers, setSelectedMembers] =
    React.useState<RowSelectionState>({});
  const [filterText, setFilterText] = React.useState('');
  const {
    data: groupRosterData,
    isLoading: isLoadingGroupRoster,
    isError: isErrorGroupRoster,
  } = groupRoster;

  const validRosterMembers = React.useMemo(() => {
    if (!groupRosterData) return [];
    const currentMemberIds = new Set(
      currentPositionMembers.map((member) => member.userId)
    );
    return groupRosterData.filter(
      (member) => !currentMemberIds.has(member.user_guid)
    );
  }, [groupRosterData, currentPositionMembers]);

  const filteredData = React.useMemo(
    () =>
      validRosterMembers.filter((member) => {
        if (member.user_name) {
          return (
            member.user_name.toLowerCase().includes(filterText.toLowerCase()) ||
            member.email_address
              .toLowerCase()
              .includes(filterText.toLowerCase())
          );
        } else {
          return member.email_address
            .toLowerCase()
            .includes(filterText.toLowerCase());
        }
      }),
    [validRosterMembers, filterText]
  );

  return (
    <div className="border-primary mb-4 rounded border ps-3 pe-3 pt-1 pb-1">
      {isErrorGroupRoster && (
        <div className="d-flex align-items-center justify-content-center text-danger">
          Error loading group roster: {groupRoster.error.message}
        </div>
      )}
      {isLoadingGroupRoster && (
        <div className="d-flex align-items-center justify-content-center">
          <Loading />
        </div>
      )}
      <div className="d-flex justify-content-between align-items-center mb-2 flex-row">
        <div className="text-secondary ms-3">
          {Object.keys(selectedMembers).length || 0} selected
        </div>
        <div style={{ maxWidth: '38vw', width: '100%' }}>
          <StylishNewInput
            value={filterText}
            onChange={(e) => {
              setFilterText(e.target.value);
              setSelectedMembers({});
            }}
            placeholder="Search members..."
          />
        </div>
      </div>
      <div className="tw:max-h-[50vh] tw:overflow-y-auto">
        <PratusTable
          columns={[
            {
              id: 'expander',
              cell: ({ row }) => {
                const expandable = row.getCanExpand?.();
                if (!expandable) return null;
                return row.getIsExpanded() ? (
                  <ExpandDownArrow onClick={() => row.toggleExpanded()} />
                ) : (
                  <ExpandRightArrow onClick={() => row.toggleExpanded()} />
                );
              },
              meta: {
                customSize: true,
                headerAlign: 'center',
                align: 'center',
              },
              size: 42,
            },
            {
              id: 'selector',
              cell: ({ row }) => {
                const selectable = row.getCanSelect?.();
                if (!selectable) return null;
                return row.getIsSelected() ? (
                  selectingLeader ? (
                    <StylishNewRadio
                      className={''}
                      disabled={false}
                      label={''}
                      id={`${row.id}-checkbox`}
                      value={''}
                      name={''}
                      checked
                      onChange={() => row.toggleSelected()}
                    />
                  ) : (
                    <StylishNewCheckbox
                      checked
                      onClick={() => row.toggleSelected()}
                    />
                  )
                ) : selectingLeader ? (
                  <StylishNewRadio
                    className={''}
                    disabled={false}
                    label={''}
                    id={`${row.id}-checkbox`}
                    value={''}
                    name={''}
                    checked={false}
                    onChange={() => row.toggleSelected()}
                  />
                ) : (
                  <StylishNewCheckbox onClick={() => row.toggleSelected()} />
                );
              },
              meta: {
                customSize: true,
                headerAlign: 'center',
                align: 'center',
              },
              size: 42,
            },
            {
              header: 'Name',
              accessorKey: 'user_name',
              cell: ({ cell }) => {
                return cell.getValue() || cell.row.original.email_address;
              },
            },
            {
              header: 'Assigned To Incidents',
              accessorKey: 'assigned_incidents',
              cell: ({ cell }) =>
                cell
                  .getValue<
                    {
                      incident_id: string;
                      incident_name: string;
                      position_name: string;
                    }[]
                  >()
                  .map((incident) => incident.incident_name)
                  .join(', '),
            },
          ]}
          data={filteredData}
          expansion={{
            getRowCanExpand(_row) {
              return true;
            },
            expandedRowModel: (row: GroupRosterItem) => {
              return (
                <div className="d-flex justify-content-left flex-row">
                  <div className="d-flex flex-column me-3 mb-1">
                    <div className="text-muted mt-1 mb-1">EMAIL</div>
                    <span>{row.email_address}</span>
                  </div>
                  <div className="d-flex flex-column mb-1">
                    <div className="text-muted mt-1 mb-1">PHONE</div>
                    <span>
                      {row.profile_settings.mobile_phone_number ||
                        row.profile_settings.home_phone_number ||
                        ''}
                    </span>
                  </div>
                </div>
              );
            },
          }}
          selection={{
            onRowSelectionChange: setSelectedMembers,
            selectedRows: selectedMembers,
            enableMultiRowSelection: !selectingLeader,
          }}
          getRowId={(row) => row.user_guid}
        />
      </div>
      <div>
        <StylishNewButton
          onClick={() => {
            onSelectMembers(
              validRosterMembers.filter(
                (member) => selectedMembers[member.user_guid]
              )
            );
            closeTable();
          }}
          disabled={Object.keys(selectedMembers).length === 0}
          className="me-3 mb-3"
        >
          {selectingLeader ? 'Set as Leader' : 'Send Activation Request'}
        </StylishNewButton>
        <StylishNewButton
          onClick={() => {
            setSelectedMembers({});
            closeTable();
          }}
          className="button--secondary"
        >
          Cancel
        </StylishNewButton>
      </div>
    </div>
  );
};

export default MemberSelectTable;
